import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getData } from "../../apis";
import { API } from "../../apis/config";
import LikeButton from "../../components/LikeButton";
import LoadingScreen from "../../components/LoadingScreen";
import ScrollToTopButton from "../../components/ScrollToTopButton";
import { ModalInfo } from "../../interfaces/ModalInfo";
import { OpeningWarehouseProduct } from "../../interfaces/OpeningWarehouseProduct";
import { RootState } from "../../stores";
import { set, setLike } from "../../stores/slices/openingWarehouseSlice";
import {
  changeHeaderFilter,
  convertFilterIcon,
} from "../../utils/headerFilter";
import { matchesProductNameSearch } from "../../utils/koreanSearch";
import {
  addItemToLocalStorageArray,
  localStorageKeys,
  removeItemFromLocalStorageArray,
} from "../../utils/localStorage";
import ProductModal from "./ProductModal";
import SearchProductInput from "../../components/SearchProductInput";
// import map from "../../assets/images/opening_warehouse_map.jpg";

function SearchProduct() {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [modal, setModal] = useState<ModalInfo<OpeningWarehouseProduct>>({
    isOpen: false,
    selected: undefined,
  });
  const dispatch = useDispatch();
  const searchRows = useSelector(
    (state: RootState) => state.product.searchRows
  );
  const [sortedData, setSortedData] = useState<OpeningWarehouseProduct[]>([]);
  const [headerFilter] = useState<{ [key: string]: number }>({});
  const productSliceData = useSelector(
    (state: RootState) => state.product.searchRows
  );

  const Header = {
    Name: "name",
    Location: "location",
    Volume: "volume",
    note: "note",
    Save: "save",
  } as const;

  const openModal = (row: OpeningWarehouseProduct) => {
    if (modal) {
      setModal({ selected: row, isOpen: true });
    }
  };

  const handleLikeClick = (
    row: OpeningWarehouseProduct
  ): OpeningWarehouseProduct => {
    const newRow = { ...row, isLike: !row.isLike, quantity: 1 };
    if (row.isLike) {
      removeItemFromLocalStorageArray(localStorageKeys.likeProduct, newRow.id);
    } else {
      addItemToLocalStorageArray(localStorageKeys.likeProduct, {
        id: newRow.id,
        isLike: newRow.isLike,
        quantity: newRow.quantity,
      });
    }
    dispatch(setLike(newRow.id));

    const tmp = sortedData.map((item) => {
      let tmp = item;
      if (tmp.id === newRow.id) {
        tmp = { ...tmp, isLike: !row.isLike };
      }
      return tmp;
    });
    setSortedData(tmp);

    return newRow;
  };

  const clickHeader = (
    key: string,
    compareFn: (
      a: OpeningWarehouseProduct,
      b: OpeningWarehouseProduct
    ) => number
  ) => {
    changeHeaderFilter(headerFilter, setSortedData, key, compareFn, searchRows);
  };

  const createFilterIcon = (key: string): string => {
    return convertFilterIcon(headerFilter, key);
  };

  useEffect(() => {
    const data = async () => {
      await getData<OpeningWarehouseProduct[]>(API.OPENING_WAREHOUSE.PRODUCTS)
        .then((res) => {
          dispatch(set(res.result.filter((value) => value.searchable)));
        })
        .finally(() => {
          setIsLoading(false);
        });
    };
    data();
  }, [dispatch]);

  useEffect(() => {
    if (sortedData.length === 0) {
      setSortedData(productSliceData);
    }
  }, [dispatch, productSliceData, sortedData.length]);

  return (
    <div>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <div className="md:mx-20">
          <ScrollToTopButton />

          <ProductModal
            handleLikeClick={handleLikeClick}
            modal={modal}
            setModal={(isOpen, selected) => {
              setModal({ isOpen, selected });
            }}
          />

          <div className="flex items-center mt-6">
            <span className="text-2xl font-bold">창고개방전 상품 검색</span>
          </div>

          {/* <div className="flex items-center justify-center mt-5">
            <img
              src={map}
              alt="map"
              className="rounded-lg w-full md:max-w-3xl"
            />
          </div> */}

          <SearchProductInput
            searchKeyword={searchKeyword}
            setSearchKeyword={setSearchKeyword}
          />
          <span className="text-sm">
            {/* 상품을 선택하여 추가 안내 사항을 확인하세요. */}
            상품명에 밑줄이 있다면 비고 사항이 있는 상품입니다.
          </span>
          <br />
          <span className="text-sm">
            테이블의 각 헤더(상품명, 위치, 저장)를
            <br className="md:hidden" />
            <span className="md:inline hidden">&nbsp;</span>
            선택하면 정렬할 수 있습니다.
          </span>
          <table className="my-4 w-full bg-white">
            <thead className="bg-gray-200">
              <tr>
                <th
                  className="w-5/12 px-4 py-2 text-center text-gray-600 cursor-pointer text-sm"
                  onClick={() => {
                    clickHeader(Header.Name, (a, b) =>
                      a.name.localeCompare(b.name, "ko")
                    );
                  }}
                >
                  상품명{createFilterIcon(Header.Name)}
                </th>
                <th
                  className="w-3/12 px-4 py-2 text-center text-gray-600 cursor-pointer text-sm"
                  onClick={() => {
                    clickHeader(Header.Location, (a, b) =>
                      a.location.localeCompare(b.location, "ko")
                    );
                  }}
                >
                  위치{createFilterIcon(Header.Location)}
                </th>
                <th
                  className="w-2/12 px-4 py-2 text-center text-gray-600 cursor-pointer text-sm"
                  onClick={() => {
                    clickHeader(Header.note, (a, b) =>
                      a.note.localeCompare(b.note, "ko")
                    );
                  }}
                >
                  비고{createFilterIcon(Header.note)}
                </th>
                <th
                  className="w-2/12 px-4 py-2 text-center text-gray-600 cursor-pointer text-sm"
                  onClick={() => {
                    clickHeader(Header.Save, (a, b) =>
                      a.isLike === b.isLike ? 0 : a.isLike ? -1 : 1
                    );
                  }}
                >
                  저장{createFilterIcon(Header.Save)}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData
                .filter((item) =>
                  matchesProductNameSearch(item.name ?? "", searchKeyword)
                )
                .map((item, key) => (
                  <tr
                    className={`cursor-pointer hover:bg-gray-100 border-t ${
                      item.note ? "underline" : ""
                    }`}
                    key={key}
                  >
                    <td
                      className="px-2 py-2 text-sm md:text-base"
                      onClick={() => openModal(item)}
                    >
                      {item.name}
                    </td>
                    <td
                      className="px-2 py-2 text-sm"
                      onClick={() => openModal(item)}
                    >
                      {item.location}
                    </td>
                    <td
                      className="px-2 py-2 text-sm"
                      onClick={() => openModal(item)}
                    >
                      {item.note.slice(0, 3)}
                    </td>
                    <td
                      className="px-2 py-2"
                      onClick={() => handleLikeClick(item)}
                    >
                      <LikeButton
                        onClick={() => {}}
                        isLike={item.isLike}
                      ></LikeButton>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SearchProduct;
